import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createTeacherChat,
  sendMessageToTeacherChat,
} from "@packages/api/madlenAPI";
import { useAppSelector } from "@packages/store/hooks";

interface AIAssistantProps {
  className?: string;
}

// Animated background component
const AnimatedBackground: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-purple-300/20 dark:from-blue-500/10 dark:to-purple-600/10 rounded-full blur-xl animate-[float_6s_ease-in-out_infinite]"></div>
      <div className="absolute top-1/3 right-1/3 w-24 h-24 bg-gradient-to-br from-pink-200/20 to-orange-300/20 dark:from-pink-500/10 dark:to-orange-600/10 rounded-full blur-lg animate-[float_4s_ease-in-out_infinite_reverse]"></div>
      <div className="absolute bottom-1/4 left-1/3 w-28 h-28 bg-gradient-to-br from-green-200/20 to-blue-300/20 dark:from-green-500/10 dark:to-blue-600/10 rounded-full blur-lg animate-[float_5s_ease-in-out_infinite]"></div>
      <div className="absolute bottom-1/3 right-1/4 w-20 h-20 bg-gradient-to-br from-violet-200/20 to-indigo-300/20 dark:from-violet-500/10 dark:to-indigo-600/10 rounded-full blur-xl animate-[float_7s_ease-in-out_infinite_reverse]"></div>

      {/* Subtle geometric shapes */}
      <div className="absolute top-1/2 left-1/6 w-4 h-4 bg-gray-300/30 dark:bg-gray-600/20 rotate-45 animate-[spin_20s_linear_infinite]"></div>
      <div className="absolute top-1/5 right-1/5 w-6 h-6 border border-gray-300/40 dark:border-gray-600/30 rotate-45 animate-[spin_15s_linear_infinite_reverse]"></div>
      <div className="absolute bottom-1/5 left-1/5 w-3 h-3 bg-gray-400/40 dark:bg-gray-500/30 rounded-full animate-pulse"></div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      ></div>
    </div>
  );
};

const AIAssistant: React.FC<AIAssistantProps> = ({ className = "" }) => {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const selectedModel = useAppSelector(
    (state) =>
      state.user?.teacher?.preferences?.preferredLanguageModel ||
      "claude-3.7-sonnet",
  );

  const createChatAndSendMessage = async (message: string) => {
    if (!message.trim() || isLoading) return;

    setIsLoading(true);

    try {
      // Create a new teacher chat
      const chat = await createTeacherChat({
        llmModel: selectedModel,
      });

      if (chat.id) {
        // Send the message to the newly created chat
        await sendMessageToTeacherChat(chat.id, {
          message: message.trim(),
          contentType: "TEXT",
        });

        // Clear the input and navigate to the new chat
        setQuery("");
        navigate(`/assistants/new/empty/${chat.id}`);
      }
    } catch (error) {
      console.error("Failed to create chat or send message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createChatAndSendMessage(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <div
      className={`relative bg-white dark:bg-dark-600 rounded-3xl border border-dark-400 dark:border-dark-400 overflow-hidden shadow-2xl shadow-gray-200/20 dark:shadow-black/40 ${className}`}
    >
      {/* Animated Background */}
      <AnimatedBackground />

      {/* Header Bar */}
      <div className="relative bg-gray-50 dark:bg-dark-600 px-6 py-4 border-b border-gray-100 dark:border-dark-400 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              AI Assistant
            </h2>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="relative p-6 lg:p-8 z-10">
        <div className="max-w-2xl mx-auto py-14">
          {/* Main Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              What can I help with?
            </h1>
          </div>

          {/* Search Textarea */}
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="relative">
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything about teaching, lesson planning, or assignments..."
                rows={3}
                disabled={isLoading}
                className="w-full px-4 py-2 pr-16 bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400 rounded-2xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 focus:border-transparent text-lg resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              />

              {/* Plus Button - Bottom Left */}
              <button
                type="button"
                className="absolute left-3 bottom-3 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>

              {/* Send Button - Bottom Right */}
              <button
                type="submit"
                disabled={isLoading || !query.trim()}
                className="absolute right-3 bottom-4 p-2 bg-gradient-to-r from-[#DD6521] via-[#CE3974] to-[#CB4D22] rounded-full shadow border border-gray-200 dark:border-dark-400 text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <svg
                    className="w-5 h-5 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 rotate-45"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                )}
              </button>
            </div>
          </form>

          {/* Example Queries */}
          <div className="mt-8 text-center">
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "Create a lesson plan for beginners",
                "Generate quiz questions",
                "Explain grammar concepts",
                "Design homework assignments",
              ].map((example, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setQuery(example);
                    // Auto-submit the example query
                    createChatAndSendMessage(example);
                  }}
                  disabled={isLoading}
                  className="px-3 py-1 bg-gray-50 dark:bg-dark-500 text-gray-600 dark:text-gray-400 rounded-full text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
